import {
  deleteBlob,
  deleteMutation,
  listMutations,
  putBlob,
  putMutation,
  type ChecklistCommentPayload,
  type ChecklistProofPayload,
  type ChecklistTogglePayload,
  type ChecklistVoiceMemoPayload,
  type HelperMutationPayload,
  type HelperMutationRecord,
  type PendingExpenseIntakePayload,
  type StockCountRoundPayload,
  type StockScanSuggestionPayload,
  type WorkspaceActivityPayload,
  type OpsDueRecordPayload,
  type OpsCheckpointScanPayload,
  type OpsSkipDuePayload,
  type OpsRaiseRequestPayload,
  type OpsAttendanceRollPayload,
  type OpsStartRequestPayload,
  type OpsMarkRequestDonePayload,
  type OpsBmwSavePayload,
  type OpsDprSavePayload,
  type OpsConIssueCreatePayload,
  type OpsDprLineBlob,
} from './helperOutboxDb'
import { setHelperQueue } from './helperStore'
import { assertMutableWorkspace } from './mutationGuard'

const generateId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `helper-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

export { createOpId } from './opId'

const refreshStore = async () => {
  const records = await listMutations()
  setHelperQueue(records)
}

const enqueue = async (payload: HelperMutationPayload): Promise<HelperMutationRecord> => {
  await assertMutableWorkspace(payload.data.workspaceId)
  const now = Date.now()
  const record: HelperMutationRecord = {
    id: generateId(),
    type: payload.type,
    payload: payload.data,
    status: 'PENDING',
    retryCount: 0,
    error: null,
    createdAt: now,
    lastUpdatedClient: now,
  }
  await putMutation(record)
  await refreshStore()
  return record
}

import {
  getSharedDeviceWorkRouterContext,
  routeChecklistComment,
  routeChecklistProof,
  routeChecklistToggle,
  routeChecklistVoiceMemo,
  routeStockCountRound,
  routeStockScanSuggestion,
  routeRegisterRecord,
  routeCheckpointScan,
  routeSkipDue,
  routeRaiseRequest,
  routeAttendanceRoll,
  routeStartRequest,
  routeMarkRequestDone,
  routeBmwSave,
  routeDprSave,
  routeConIssueCreate,
} from '../sharedDeviceWorkRouter'

export const queueChecklistToggle = (data: ChecklistTogglePayload) => {
  const routerCtx = getSharedDeviceWorkRouterContext()
  if (routerCtx) {
    return routeChecklistToggle(data, routerCtx)
  }
  return enqueue({ type: 'CHECKLIST_TOGGLE', data })
}

export const queueChecklistProof = async (
  data: Omit<ChecklistProofPayload, 'blobKey'>,
  file: Blob,
): Promise<HelperMutationRecord> => {
  const actionTime = Date.now()
  const routerCtx = getSharedDeviceWorkRouterContext()
  if (routerCtx) {
    return routeChecklistProof(data, file, routerCtx, actionTime)
  }
  await assertMutableWorkspace(data.workspaceId)
  const blobKey = generateId()
  await putBlob({ key: blobKey, blob: file, contentType: data.contentType, createdAt: actionTime })
  return enqueue({ type: 'CHECKLIST_PROOF_UPLOAD', data: { ...data, blobKey } })
}

export const queueChecklistComment = (data: ChecklistCommentPayload) => {
  const actionTime = Date.now()
  const routerCtx = getSharedDeviceWorkRouterContext()
  if (routerCtx) {
    return routeChecklistComment(data, routerCtx, actionTime)
  }
  return enqueue({ type: 'CHECKLIST_COMMENT', data })
}

export const queueChecklistVoiceMemo = async (
  data: Omit<ChecklistVoiceMemoPayload, 'blobKey'>,
  audio: Blob,
): Promise<HelperMutationRecord> => {
  const actionTime = Date.now()
  const routerCtx = getSharedDeviceWorkRouterContext()
  if (routerCtx) {
    return routeChecklistVoiceMemo(data, audio, routerCtx, actionTime)
  }
  await assertMutableWorkspace(data.workspaceId)
  const blobKey = generateId()
  await putBlob({ key: blobKey, blob: audio, contentType: 'audio/webm', createdAt: actionTime })
  return enqueue({ type: 'CHECKLIST_VOICE_MEMO', data: { ...data, blobKey } })
}

export const queueWorkspaceActivity = (data: WorkspaceActivityPayload) =>
  enqueue({ type: 'WORKSPACE_ACTIVITY', data })

export const queueStockCountRound = (data: StockCountRoundPayload) => {
  const routerCtx = getSharedDeviceWorkRouterContext()
  if (routerCtx) {
    return routeStockCountRound(data, routerCtx)
  }
  return enqueue({ type: 'STOCK_COUNT_ROUND', data })
}

export const queueStockScanSuggestion = (data: StockScanSuggestionPayload) => {
  const routerCtx = getSharedDeviceWorkRouterContext()
  if (routerCtx) {
    return routeStockScanSuggestion(data, routerCtx)
  }
  return enqueue({ type: 'STOCK_SCAN_SUGGESTION', data })
}

export const queuePendingExpenseIntake = async (
  data: Omit<PendingExpenseIntakePayload, 'blobKey'>,
  file: Blob,
): Promise<HelperMutationRecord> => {
  await assertMutableWorkspace(data.workspaceId)
  const blobKey = generateId()
  await putBlob({ key: blobKey, blob: file, contentType: data.contentType, createdAt: Date.now() })
  return enqueue({ type: 'PENDING_EXPENSE_INTAKE', data: { ...data, blobKey } })
}

export const queueOpsDueRecord = (data: OpsDueRecordPayload) => {
  const routerCtx = getSharedDeviceWorkRouterContext()
  if (routerCtx) {
    return routeRegisterRecord(data, routerCtx)
  }
  return enqueue({ type: 'OPS_DUE_RECORD', data })
}

export const queueOpsCheckpointScan = (data: OpsCheckpointScanPayload) => {
  const routerCtx = getSharedDeviceWorkRouterContext()
  if (routerCtx) {
    return routeCheckpointScan(data, routerCtx)
  }
  return enqueue({ type: 'OPS_CHECKPOINT_SCAN', data })
}

export const queueOpsSkipDue = (data: OpsSkipDuePayload) => {
  const routerCtx = getSharedDeviceWorkRouterContext()
  if (routerCtx) {
    return routeSkipDue(data, routerCtx)
  }
  return enqueue({ type: 'OPS_SKIP_DUE', data })
}

export const queueOpsRaiseRequest = (data: OpsRaiseRequestPayload) => {
  const routerCtx = getSharedDeviceWorkRouterContext()
  if (routerCtx) {
    return routeRaiseRequest(data, routerCtx)
  }
  return enqueue({ type: 'OPS_RAISE_REQUEST', data })
}

export const queueOpsAttendanceRoll = (data: OpsAttendanceRollPayload) => {
  const routerCtx = getSharedDeviceWorkRouterContext()
  if (routerCtx) {
    return routeAttendanceRoll(data, routerCtx)
  }
  return enqueue({ type: 'OPS_ATTENDANCE_ROLL', data })
}

export const queueOpsStartRequest = (data: OpsStartRequestPayload) => {
  const routerCtx = getSharedDeviceWorkRouterContext()
  if (routerCtx) {
    return routeStartRequest(data, routerCtx)
  }
  return enqueue({ type: 'OPS_START_REQUEST', data })
}

export const queueOpsMarkRequestDone = async (
  data: OpsMarkRequestDonePayload,
  fileOrFiles?: Blob | Blob[] | File | File[],
): Promise<HelperMutationRecord> => {
  const actionTime = Date.now()
  const routerCtx = getSharedDeviceWorkRouterContext()
  if (routerCtx) {
    return routeMarkRequestDone(data, routerCtx, fileOrFiles, actionTime)
  }
  await assertMutableWorkspace(data.workspaceId)
  let blobKey = data.blobKey
  const blobKeys: string[] = data.blobKeys ? [...data.blobKeys] : []

  const files = fileOrFiles
    ? Array.isArray(fileOrFiles)
      ? fileOrFiles
      : [fileOrFiles]
    : []

  for (const f of files) {
    const k = generateId()
    await putBlob({ key: k, blob: f, contentType: f.type || 'image/jpeg', createdAt: actionTime })
    blobKeys.push(k)
  }

  if (!blobKey && blobKeys.length > 0) {
    blobKey = blobKeys[0]
  }

  return enqueue({
    type: 'OPS_MARK_REQUEST_DONE',
    data: {
      ...data,
      ...(blobKey ? { blobKey, blobKeys } : {}),
    },
  })
}

export const removeMutation = async (id: string): Promise<void> => {
  const records = await listMutations()
  const entry = records.find((record) => record.id === id)
  if (!entry) return
  if (
    entry.type === 'CHECKLIST_PROOF_UPLOAD' ||
    entry.type === 'CHECKLIST_VOICE_MEMO' ||
    entry.type === 'PENDING_EXPENSE_INTAKE' ||
    entry.type === 'OPS_MARK_REQUEST_DONE' ||
    entry.type === 'OPS_CON_ISSUE_CREATE'
  ) {
    const payload = entry.payload as { blobKey?: string; blobKeys?: string[] }
    if (payload.blobKey) await deleteBlob(payload.blobKey)
    if (Array.isArray(payload.blobKeys)) {
      for (const k of payload.blobKeys) {
        if (k !== payload.blobKey) await deleteBlob(k)
      }
    }
  }
  if (entry.type === 'OPS_DPR_SAVE') {
    const payload = entry.payload as OpsDprSavePayload
    if (payload.lineBlobs) {
      for (const lb of payload.lineBlobs) {
        if (lb.blobKey) await deleteBlob(lb.blobKey)
        if (Array.isArray(lb.blobKeys)) {
          for (const k of lb.blobKeys) {
            if (k !== lb.blobKey) await deleteBlob(k)
          }
        }
      }
    }
  }
  await deleteMutation(id)
  await refreshStore()
}

export const queueOpsBmwSave = (data: OpsBmwSavePayload) => {
  const routerCtx = getSharedDeviceWorkRouterContext()
  if (routerCtx) {
    return routeBmwSave(data, routerCtx)
  }
  return enqueue({ type: 'OPS_BMW_SAVE', data })
}

export const queueOpsDprSave = async (
  data: OpsDprSavePayload,
  lineBlobs?: OpsDprLineBlob[],
  lineFiles?: Array<{ lineId: string; file: Blob | File }>,
): Promise<HelperMutationRecord> => {
  const routerCtx = getSharedDeviceWorkRouterContext()
  if (routerCtx) {
    return routeDprSave(data, routerCtx, lineBlobs, lineFiles)
  }
  await assertMutableWorkspace(data.workspaceId)
  const actionTime = Date.now()
  const blobsToStore: OpsDprLineBlob[] = lineBlobs ? [...lineBlobs] : []
  if (lineFiles && lineFiles.length > 0) {
    for (const lf of lineFiles) {
      const k = generateId()
      await putBlob({ key: k, blob: lf.file, contentType: lf.file.type || 'image/jpeg', createdAt: actionTime })
      blobsToStore.push({ lineId: lf.lineId, blobKey: k, blobKeys: [k] })
    }
  }

  return enqueue({
    type: 'OPS_DPR_SAVE',
    data: {
      ...data,
      ...(blobsToStore.length > 0 ? { lineBlobs: blobsToStore } : {}),
    },
  })
}

export const queueOpsConIssueCreate = async (
  data: OpsConIssueCreatePayload,
  files?: File[] | Blob[],
): Promise<HelperMutationRecord> => {
  const routerCtx = getSharedDeviceWorkRouterContext()
  if (routerCtx) {
    return routeConIssueCreate(data, routerCtx, files)
  }
  await assertMutableWorkspace(data.workspaceId)
  const actionTime = Date.now()
  const blobKeys: string[] = data.blobKeys ? [...data.blobKeys] : []

  if (files && files.length > 0) {
    for (const f of files) {
      const k = generateId()
      await putBlob({ key: k, blob: f, contentType: f.type || 'image/jpeg', createdAt: actionTime })
      blobKeys.push(k)
    }
  }

  return enqueue({
    type: 'OPS_CON_ISSUE_CREATE',
    data: {
      ...data,
      ...(blobKeys.length > 0 ? { blobKeys } : {}),
    },
  })
}

export const undoLastMutation = async (): Promise<HelperMutationRecord | null> => {
  const records = await listMutations()
  const sorted = records
    .filter((record) => record.status === 'PENDING')
    .sort((a, b) => b.createdAt - a.createdAt)
  const last = sorted[0]
  if (!last) return null
  await removeMutation(last.id)
  return last
}

export const refreshHelperQueue = refreshStore
